import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRequest } from "services/api";
import Swal from "sweetalert2";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const WorkshopSupplierContext = createContext();

export const WorkshopSupplierProvider = ({ children }) => {
    const [filterOptions, setFilterOptions] = useState({
        search: "",
        currentPage: 1,
    });

    const queryString = buildQueryString({
        search: filterOptions.search || "",
        page: filterOptions.currentPage,
        paginate: LIMIT_DATA_PER_PAGE,
    });

    const {
        data: suppliers,
        error,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ["workshop-suppliers", filterOptions],
        queryFn: async () => {
            const response = await getRequest(`/workshop-suppliers${queryString}`);
            if (!response.success) {
                throw new Error(response.message || "Failed to fetch workshop suppliers.");
            }
            return response;
        },
        keepPreviousData: true,
    });

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            if (!e.target.value.trim()) {
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "warning",
                    title: "Search input cannot be empty!",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                return;
            }
            setFilterOptions((prev) => ({
                ...prev,
                search: e.target.value,
                currentPage: 1,
            }));
        }
    };

    const handlePageChange = (page) => {
        setFilterOptions((prev) => ({
            ...prev,
            currentPage: page,
        }));
    };

    const totalPages = suppliers?.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5,
    });

    return (
        <WorkshopSupplierContext.Provider
            value={{
                suppliers: suppliers?.data || [],
                isLoading,
                error,
                filterOptions,
                handleSearch,
                refetch,
                Pagination,
            }}
        >
            {children}
        </WorkshopSupplierContext.Provider>
    );
};

export const useWorkshopSupplier = () => {
    const context = useContext(WorkshopSupplierContext);
    if (!context) {
        throw new Error("useWorkshopSupplier must be used within WorkshopSupplierProvider");
    }
    return context;
};
